import { useState } from "react";
import { CheckCircle2, Link2, MousePointer2, Sparkles } from "lucide-react";
import { cn } from "../../lib/cn";
import { GroqLogo } from "../common/GroqLogo";
import { Badge } from "../ui/Badge";
import { Score } from "../ui/Score";
import { AgenticBrowsingPassScore } from "../metrics/agentic/AgenticBrowsingPassScore";
import { SuggestionIssueCard } from "../ai/SuggestionCards";

const EXAMPLE_ISSUE = {
  title: "Reduce unused JavaScript",
  finding: "Est. savings of 156 KiB",
  affected: ["https://www.googletagmanager.com/gtm.js?id=GTM-XXXX"],
  fix: [
    'Next.js: load GTM with next/script and strategy="afterInteractive" in app/layout.',
    "Other stacks: use Partytown or the framework script helper so GTM stays off the critical path.",
  ],
};

function ShowcaseFrame({ children, className }) {
  return (
    <div
      className={cn(
        "relative rounded-xl border border-border bg-card shadow-sm overflow-hidden",
        className,
      )}
    >
      <div className="p-4 sm:p-5">{children}</div>
    </div>
  );
}

function ExpandHintCallout() {
  return (
    <div className="relative mt-4 px-4 pb-1 w-full flex justify-center">
      <div className="relative pt-5 -translate-x-2 sm:-translate-x-8">
        <MousePointer2
          size={16}
          className="absolute left-0 top-0 -translate-x-1 -translate-y-1 text-muted-foreground/45 shrink-0 pointer-events-none"
          strokeWidth={1.75}
        />
        <div className="inline-flex items-center rounded-md border border-border/80 bg-background/90 px-2 py-0.5 shadow-[0_1px_2px_rgba(0,0,0,0.04)] ring-1 ring-black/[0.02]">
          <span className="text-[9px] font-mono uppercase tracking-[0.12em] text-muted-foreground">
            Click to expand
          </span>
        </div>
      </div>
    </div>
  );
}

function AiFixVisual() {
  const [expanded, setExpanded] = useState(false);

  return (
    <div className="relative w-full min-w-0">
      <div className="rounded-xl border border-border bg-card shadow-sm overflow-hidden">
        <div className="flex items-center gap-2 border-b border-border/60 bg-muted/30 px-3 py-2 sm:px-4 sm:py-2.5 min-w-0">
          <span className="h-2.5 w-2.5 rounded-full bg-red-400/70 shrink-0" />
          <span className="h-2.5 w-2.5 rounded-full bg-amber-400/70 shrink-0" />
          <span className="h-2.5 w-2.5 rounded-full bg-green-400/70 shrink-0" />
          <span className="ml-1 sm:ml-2 text-[11px] sm:text-xs text-muted-foreground font-mono truncate min-w-0">
            pagetest.in / test
          </span>
        </div>

        <div className="p-3 sm:p-5 space-y-4">
          <div className="space-y-3">
            <div className="grid w-full grid-cols-4 gap-x-1 sm:gap-4 justify-items-center [&>div]:gap-0.5 sm:[&>div]:gap-2 [&>div>div:first-child]:!size-[3.25rem] sm:[&>div>div:first-child]:!size-20 [&>div>div:first-child_span]:!text-sm sm:[&>div>div:first-child_span]:!text-xl [&>div>span:last-child]:!text-[8px] sm:[&>div>span:last-child]:!text-xs [&>div>span:last-child]:max-w-[4.25rem] [&>div>span:last-child]:leading-[1.15] [&>div>div:first-child>div]:!px-1.5 [&>div>div:first-child>div]:!py-0.5 [&>div>div:first-child>div]:!gap-1 [&>div>div:first-child>div_span]:!text-[11px] sm:[&>div>div:first-child>div_span]:!text-base">
              <Score value={78} label="Performance" size="xs" />
              <Score value={96} label="Accessibility" size="xs" />
              <Score value={100} label="Best Practices" size="xs" />
              <AgenticBrowsingPassScore passCount={3} totalCount={3} size="xs" />
            </div>
            <Badge
              variant="outline"
              className="text-[10px] sm:text-xs font-normal shrink-0 w-fit"
            >
              Next.js detected
            </Badge>
          </div>

          <SuggestionIssueCard
            issue={EXAMPLE_ISSUE}
            defaultOpen={false}
            onOpenChange={setExpanded}
          />
        </div>
      </div>

      {!expanded && <ExpandHintCallout />}
    </div>
  );
}

function ShareableReportVisual() {
  return (
    <ShowcaseFrame>
      <div className="rounded-lg border border-border p-4 space-y-4">
        <div className="flex items-center gap-2">
          <Link2 size={14} className="text-brand shrink-0" />
          <p className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground">
            Share link copied
          </p>
        </div>
        <p className="text-xs font-mono text-muted-foreground break-all rounded-lg border border-border bg-muted/40 px-3 py-2">
          pagetest.app/r/a8K2m9xQ
        </p>
        <div className="grid grid-cols-2 gap-3">
          {[
            { device: "Desktop", perf: 91 },
            { device: "Mobile", perf: 84 },
          ].map((col) => (
            <div
              key={col.device}
              className="rounded-lg border border-border bg-muted/30 p-3 text-center"
            >
              <p className="text-[10px] font-mono uppercase text-muted-foreground">
                {col.device}
              </p>
              <p className="mt-1 text-2xl font-semibold tabular-nums text-brand">
                {col.perf}
              </p>
            </div>
          ))}
        </div>
        <p className="text-xs text-muted-foreground">
          Desktop and mobile in one link. Clients open it without an account.
        </p>
      </div>
    </ShowcaseFrame>
  );
}

function CodeContextVisual() {
  return (
    <ShowcaseFrame>
      <div className="space-y-3">
        <Badge variant="outline" className="text-xs font-normal">
          Next.js detected
        </Badge>
        <div className="rounded-lg border border-border bg-muted/30 p-3 space-y-2">
          <p className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground">
            Context sent to AI
          </p>
          <ul className="space-y-1.5 text-[11px] font-mono">
            <li className="truncate text-foreground">app/layout.tsx</li>
            <li className="truncate text-muted-foreground">
              googletagmanager.com/gtm.js
            </li>
            <li className="truncate text-muted-foreground">
              /_next/static/chunks/main.js
            </li>
          </ul>
        </div>
        <p className="text-xs text-muted-foreground">
          Fixes reference your stack, files, and the scripts Lighthouse flagged.
        </p>
      </div>
    </ShowcaseFrame>
  );
}

function AuditMetaVisual() {
  return (
    <ShowcaseFrame>
      <div className="space-y-2">
        {[
          { tag: "og:title", status: "ok", note: "Present · 58 chars" },
          {
            tag: "og:description",
            status: "warn",
            note: "Missing · falls back to meta description",
          },
          {
            tag: "og:image",
            status: "error",
            note: "Missing · no preview image for shares",
          },
          { tag: "twitter:card", status: "ok", note: "summary_large_image" },
        ].map((row) => (
          <div
            key={row.tag}
            className="flex flex-col gap-1 rounded-lg border border-border px-3 py-2.5 sm:flex-row sm:items-start sm:justify-between sm:gap-3"
          >
            <p className="text-[11px] font-mono text-foreground shrink-0">{row.tag}</p>
            <p
              className={cn(
                "text-[10px] sm:text-right sm:max-w-[11rem]",
                row.status === "error"
                  ? "text-red-600"
                  : row.status === "warn"
                    ? "text-orange-600"
                    : "text-muted-foreground",
              )}
            >
              {row.note}
            </p>
          </div>
        ))}
      </div>
      <p className="mt-4 text-xs text-muted-foreground">
        Every run flags missing or weak Open Graph and Twitter tags before you
        ship.
      </p>
    </ShowcaseFrame>
  );
}

function AutomateOgVisual() {
  return (
    <ShowcaseFrame>
      <div className="flex items-center gap-2 mb-3">
        <Badge variant="outline" className="text-xs font-normal gap-1">
          <Sparkles size={12} className="text-brand" />
          AI suggested
        </Badge>
      </div>
      <div className="rounded-lg border border-border overflow-hidden bg-muted/20">
        <div className="h-24 bg-gradient-to-br from-brand/20 to-muted flex items-center justify-center">
          <Sparkles size={20} className="text-brand" />
        </div>
        <div className="p-3 space-y-1 bg-card">
          <p className="text-[10px] uppercase text-muted-foreground">
            acme.com
          </p>
          <p className="text-sm font-semibold text-foreground">
            Acme · Performance testing for shipping teams
          </p>
          <p className="text-xs text-muted-foreground line-clamp-2">
            Run desktop and mobile audits, get stack-aware fixes, and share
            results in one link.
          </p>
        </div>
      </div>
      <p className="mt-4 text-xs text-muted-foreground">
        Generate og:title, description, and image copy from page content.
        Preview before you publish.
      </p>
    </ShowcaseFrame>
  );
}

function DriveExportVisual() {
  return (
    <ShowcaseFrame>
      <div className="overflow-x-auto -mx-1 px-1">
        <div className="rounded-lg border border-border overflow-hidden text-[10px] sm:text-[11px] font-mono min-w-[17rem]">
        <div className="grid grid-cols-4 gap-px bg-border">
          {["URL", "Perf", "SEO", "Date"].map((h) => (
            <div
              key={h}
              className="bg-muted/50 px-2 py-1.5 text-muted-foreground"
            >
              {h}
            </div>
          ))}
        </div>
        {[
          ["acme.com", "94", "98", "Jun 27"],
          ["shop.acme.com", "81", "92", "Jun 27"],
          ["blog.acme.com", "88", "95", "Jun 20"],
        ].map((row) => (
          <div key={row[0]} className="grid grid-cols-4 gap-px bg-border">
            {row.map((cell) => (
              <div
                key={cell}
                className="bg-card px-2 py-2 text-foreground truncate"
              >
                {cell}
              </div>
            ))}
          </div>
        ))}
        </div>
      </div>
      <p className="mt-4 flex items-center gap-1.5 text-xs text-muted-foreground">
        <CheckCircle2 size={14} className="text-brand shrink-0" />
        Saved to Google Drive after each test
      </p>
    </ShowcaseFrame>
  );
}

function HistoryVisual() {
  return (
    <ShowcaseFrame>
      <div className="space-y-2">
        {[
          { url: "acme.com", when: "Today, 2:14 PM", perf: 94 },
          { url: "acme.com", when: "Jun 20", perf: 89 },
          { url: "acme.com", when: "Jun 13", perf: 87 },
        ].map((row, i) => (
          <div
            key={`${row.when}-${i}`}
            className="flex items-center justify-between gap-3 rounded-lg border border-border px-3 py-2.5"
          >
            <div className="min-w-0">
              <p className="text-xs font-medium truncate">{row.url}</p>
              <p className="text-[10px] text-muted-foreground mt-0.5">
                {row.when}
              </p>
            </div>
            <span className="text-sm font-semibold tabular-nums text-brand">
              {row.perf}
            </span>
          </div>
        ))}
      </div>
    </ShowcaseFrame>
  );
}

function ScheduledVisual() {
  return (
    <ShowcaseFrame>
      <div className="rounded-lg border border-border p-4 space-y-3">
        <div className="flex items-center justify-between gap-2">
          <p className="text-sm font-medium text-foreground">acme.com</p>
          <Badge className="bg-brand/10 text-brand border-0 text-[10px]">
            Weekly
          </Badge>
        </div>
        <div className="text-xs text-muted-foreground space-y-1">
          <p>Next run: Monday 9:00 AM UTC</p>
          <p>Last run: Performance 94 · SEO 98</p>
        </div>
        <p className="text-[11px] text-muted-foreground pt-1 border-t border-border">
          Results export to your mapped Drive file automatically.
        </p>
      </div>
    </ShowcaseFrame>
  );
}

function GroqBadge({ className }) {
  return (
    <a
      href="https://groq.com/"
      target="_blank"
      rel="noopener noreferrer"
      className={cn(
        "group inline-flex items-center rounded-lg border border-border bg-background px-2.5 py-2",
        "hover:border-foreground/20 transition-colors",
        className,
      )}
    >
      <GroqLogo alt="Groq" className="h-4 w-auto" />
    </a>
  );
}

function GroqSpeedVisual() {
  return (
    <ShowcaseFrame>
      <div className="flex items-start justify-between gap-3 mb-4">
        <GroqBadge />
        <span className="rounded-full bg-brand/10 px-2 py-0.5 text-[10px] font-mono tabular-nums text-brand">
          1.2s
        </span>
      </div>
      <div className="rounded-lg border border-border bg-muted/20 p-3 space-y-2">
        <p className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground">
          Reduce unused JavaScript
        </p>
        <p className="text-xs text-foreground leading-relaxed">
          Load GTM with{" "}
          <span className="font-mono text-[11px] bg-muted px-1 rounded">
            next/script
          </span>{" "}
          and{" "}
          <span className="font-mono text-[11px] bg-muted px-1 rounded">
            afterInteractive
          </span>{" "}
          so analytics stays off the critical path.
        </p>
      </div>
      <p className="mt-4 text-xs text-muted-foreground">
        Groq LPU inference keeps fix suggestions fast enough to read while the
        audit is still on screen.
      </p>
    </ShowcaseFrame>
  );
}

function GroqMetaVisual() {
  return (
    <ShowcaseFrame>
      <div className="flex items-center justify-between gap-2 mb-3">
        <Badge variant="outline" className="text-xs font-normal gap-1">
          <Sparkles size={12} className="text-brand" />
          AI improved
        </Badge>
        <GroqBadge className="scale-90 origin-right" />
      </div>
      <div className="space-y-2 text-[11px] font-mono">
        {[
          { tag: "og:title", before: "Home", after: "Acme · Ship faster pages" },
          {
            tag: "og:description",
            before: "Missing",
            after: "Run audits, get fixes, share one link.",
          },
        ].map((row) => (
          <div
            key={row.tag}
            className="rounded-lg border border-border px-3 py-2 space-y-1"
          >
            <p className="text-muted-foreground">{row.tag}</p>
            <p className="text-red-600/80 line-through">{row.before}</p>
            <p className="text-foreground">{row.after}</p>
          </div>
        ))}
      </div>
      <p className="mt-4 text-xs text-muted-foreground">
        Social meta rewrites run on GroqCloud – preview before you publish.
      </p>
    </ShowcaseFrame>
  );
}

function GroqModelsVisual() {
  return (
    <ShowcaseFrame>
      <GroqBadge className="mb-4" />
      <div className="space-y-2">
        {[
          { name: "GPT-OSS 20B", hint: "Default · fastest", active: true },
          { name: "GPT-OSS 120B", hint: "Highest quality", active: false },
          { name: "Qwen 3.6 27B", hint: "Balanced quality", active: false },
        ].map((model) => (
          <div
            key={model.name}
            className={cn(
              "flex items-center justify-between gap-3 rounded-lg border px-3 py-2.5",
              model.active
                ? "border-brand/40 bg-brand/[0.04]"
                : "border-border bg-muted/20",
            )}
          >
            <div className="min-w-0">
              <p className="text-xs font-medium text-foreground truncate">
                {model.name}
              </p>
              <p className="text-[10px] text-muted-foreground">{model.hint}</p>
            </div>
            {model.active && (
              <span className="shrink-0 text-[10px] font-mono text-brand">
                Active
              </span>
            )}
          </div>
        ))}
      </div>
      <p className="mt-4 text-xs text-muted-foreground">
        Pick speed or quality. All models run on{" "}
        <a
          href="https://groq.com/"
          target="_blank"
          rel="noopener noreferrer"
          className="text-brand hover:underline"
        >
          Groq inference
        </a>
        .
      </p>
    </ShowcaseFrame>
  );
}

const VISUALS = {
  "ai-fix": AiFixVisual,
  share: ShareableReportVisual,
  "code-context": CodeContextVisual,
  "audit-meta": AuditMetaVisual,
  "automate-og": AutomateOgVisual,
  drive: DriveExportVisual,
  history: HistoryVisual,
  scheduled: ScheduledVisual,
  "groq-speed": GroqSpeedVisual,
  "groq-meta": GroqMetaVisual,
  "groq-models": GroqModelsVisual,
};

export function FeatureShowcase({
  headline,
  proof,
  features,
  reverse = false,
}) {
  const [active, setActive] = useState(0);

  return (
    <section className="py-16 sm:py-20">
      <h2 className="text-2xl sm:text-3xl font-[450] text-foreground tracking-[-0.06em] max-w-xl">
        {headline}
      </h2>

      <div
        className={cn(
          "mt-10 grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-14 xl:gap-16 items-start",
          reverse && "lg:[direction:rtl] lg:*:[direction:ltr]",
        )}
      >
        <div className="order-2 lg:order-none lg:max-w-md xl:max-w-lg lg:pt-2 min-w-0">
          {proof && (
            <p className="text-base sm:text-lg text-muted-foreground font-normal">
              {proof}
            </p>
          )}

          <ul className="mt-10">
            {features.map((feature, index) => {
              const isActive = index === active;

              return (
                <li key={feature.label}>
                  <button
                    type="button"
                    aria-current={isActive ? "true" : undefined}
                    onMouseEnter={() => setActive(index)}
                    onFocus={() => setActive(index)}
                    onClick={() => setActive(index)}
                    className={cn(
                      "group w-full text-left py-3.5 border-b border-border/60 last:border-b-0",
                      "focus:outline-none focus-visible:ring-2 focus-visible:ring-brand/40 rounded-sm",
                    )}
                  >
                    <span
                      className={cn(
                        "block text-[13px] font-mono uppercase tracking-normal transition-[color,opacity] duration-200",
                        isActive
                          ? "text-foreground font-semibold opacity-100"
                          : "text-muted-foreground font-medium opacity-70 group-hover:text-foreground group-hover:opacity-100",
                      )}
                    >
                      {feature.label}
                    </span>
                  </button>
                </li>
              );
            })}
          </ul>
        </div>

        <div className="order-1 lg:order-none grid w-full min-w-0 min-h-[16rem] sm:min-h-[18rem]">
          {(() => {
            const feature = features[active];
            const Visual = VISUALS[feature.visual] || AiFixVisual;
            return (
              <div key={feature.label} className="w-full self-start">
                <Visual />
              </div>
            );
          })()}
        </div>
      </div>
    </section>
  );
}

export const HOMEPAGE_SHOWCASES = [
  {
    headline: "Turn the failing audit into a fix you can ship",
    proof:
      "From the first failed check to a fix your team can review, share, and ship without rebuilding context from scratch.",
    reverse: true,
    features: [
      { label: "Context-aware fixes", visual: "ai-fix" },
      { label: "Shareable report", visual: "share" },
      { label: "Audit meta", visual: "audit-meta" },
      { label: "AI-powered", visual: "automate-og" },
    ],
  },
  {
    headline: "Keep scores where your team already works",
    proof:
      "Run once or on a cadence. PageTest keeps scores, history, and exports where your team already tracks work.",
    features: [
      { label: "Google Drive export", visual: "drive" },
      { label: "Test history", visual: "history" },
      { label: "Automate testing", visual: "scheduled" },
    ],
  },
  {
    headline: "Fast AI inference for every audit",
    proof:
      "PageTest runs on Groq – low-latency inference that turns Lighthouse findings into fixes without the wait.",
    reverse: true,
    features: [
      { label: "Instant fix suggestions", visual: "groq-speed" },
      { label: "Social meta rewrite", visual: "groq-meta" },
      { label: "Choose your model", visual: "groq-models" },
    ],
  },
];
